import React from 'react';

export default function PowerPointHelp() {
  const [addinVersion, setAddinVersion] = React.useState<string>('...');
  const [downloadUrl, setDownloadUrl] = React.useState<string>('');

  React.useEffect(() => {
    // Fetch the current add-in version from the downloads page (relative URL to avoid CORS/Cloudflare)
    fetch('/downloads/addin/version.json')
      .then(res => res.ok ? res.json() : Promise.reject())
      .then(data => {
        const version = data.version || '1.4.3';
        const installer = data.installer || `QuickQuizAddIn-Setup-v${version}.msi`;
        setAddinVersion(version);
        setDownloadUrl(`/downloads/addin/${installer}`);
      })
      .catch(() => {
        setAddinVersion('1.4.3');
        setDownloadUrl('/downloads/addin/QuickQuizAddIn-Setup-v1.4.3.msi');
      });
  }, []);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-gradient-to-r from-indigo-600 to-purple-600 rounded-xl p-6 text-white shadow-lg">
        <h1 className="text-3xl font-bold mb-2">💻 PowerPoint Add-in</h1>
        <p className="text-indigo-100">Create interactive quizzes directly in PowerPoint</p>
      </div>

      {/* Download Section */}
      <div className="bg-white rounded-xl shadow-lg p-6 border">
        <h2 className="text-2xl font-bold mb-4 text-gray-800">📥 Download & Install</h2>
        <div className="space-y-4">
          <p className="text-gray-700">
            The QuickQuiz PowerPoint Add-in allows you to create quizzes, generate QR codes, 
            and insert quiz slides directly from PowerPoint.
          </p>
          
          <a
            href={downloadUrl}
            download
            className="inline-block bg-gradient-to-r from-indigo-600 to-purple-600 text-white px-6 py-3 rounded-lg font-semibold hover:from-indigo-700 hover:to-purple-700 transition-all"
          >
            ⬇️ Download MSI Installer (v{addinVersion})
          </a>
          
          <div className="bg-yellow-50 border-l-4 border-yellow-400 p-4 rounded">
            <p className="text-sm text-yellow-800">
              <strong>ℹ️ About the Installer:</strong>
            </p>
            <p className="text-sm text-yellow-700 mt-2">
              The installer is a Windows Installer (MSI) package that is self-signed. Windows SmartScreen will display 
              a warning about an unrecognized app, but you can safely bypass this by clicking "More info" then "Run anyway".
            </p>
            <p className="text-sm text-yellow-700 mt-2">
              This MSI installer bypasses the certificate trust issues that affected the previous ClickOnce deployment. 
              The add-in is safe to install and will register properly with PowerPoint.
            </p>
            <p className="text-sm font-semibold text-yellow-800 mt-2">
              A commercially signed installer is planned for a future release to eliminate the SmartScreen warning.
            </p>
          </div>
        </div>
      </div>

      {/* Installation Steps */}
      <div className="bg-white rounded-xl shadow-lg p-6 border">
        <h2 className="text-2xl font-bold mb-4 text-gray-800">🛠️ Installation Steps</h2>
        <ol className="list-decimal pl-6 space-y-3 text-gray-700">
          <li><strong>Close PowerPoint</strong> if it's currently open</li>
          <li><strong>Download</strong> the installer using the link above</li>
          <li><strong>Run</strong> the downloaded QuickQuizAddIn-Setup-v{addinVersion}.msi file</li>
          <li>If Windows SmartScreen appears, click <strong>"More info"</strong> then <strong>"Run anyway"</strong></li>
          <li>Follow the installation wizard prompts (click <strong>"Next"</strong> → <strong>"I Agree"</strong> → <strong>"Install"</strong>)</li>
          <li>Wait for the installation to complete (may take 30-60 seconds)</li>
          <li><strong>Open PowerPoint</strong> - you should see the QuickQuiz tab in the ribbon</li>
        </ol>
      </div>

      {/* Features */}
      <div className="bg-white rounded-xl shadow-lg p-6 border">
        <h2 className="text-2xl font-bold mb-4 text-gray-800">✨ Features</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="flex items-start gap-3">
            <div className="text-2xl">🎯</div>
            <div>
              <h3 className="font-semibold text-gray-800">Create Quizzes</h3>
              <p className="text-sm text-gray-600">Build interactive quizzes from PowerPoint</p>
            </div>
          </div>
          <div className="flex items-start gap-3">
            <div className="text-2xl">📱</div>
            <div>
              <h3 className="font-semibold text-gray-800">QR Code Generation</h3>
              <p className="text-sm text-gray-600">Auto-generate QR codes for easy joining</p>
            </div>
          </div>
          <div className="flex items-start gap-3">
            <div className="text-2xl">⚡</div>
            <div>
              <h3 className="font-semibold text-gray-800">Real-time Sync</h3>
              <p className="text-sm text-gray-600">See responses update live during presentations</p>
            </div>
          </div>
          <div className="flex items-start gap-3">
            <div className="text-2xl">🚀</div>
            <div>
              <h3 className="font-semibold text-gray-800">Slide Insertion</h3>
              <p className="text-sm text-gray-600">Automatically insert quiz and join slides</p>
            </div>
          </div>
        </div>
      </div>

      {/* System Requirements */}
      <div className="bg-white rounded-xl shadow-lg p-6 border">
        <h2 className="text-2xl font-bold mb-4 text-gray-800">💻 System Requirements</h2>
        <ul className="space-y-2 text-gray-700">
          <li className="flex items-center gap-2">
            <span className="text-green-600">✓</span>
            Windows 10 or 11
          </li>
          <li className="flex items-center gap-2">
            <span className="text-green-600">✓</span>
            PowerPoint 2016 or later (Office 365, 2019, or 2021)
          </li>
          <li className="flex items-center gap-2">
            <span className="text-green-600">✓</span>
            .NET Framework 4.8 (auto-installed if missing)
          </li>
          <li className="flex items-center gap-2">
            <span className="text-green-600">✓</span>
            Approximately 50 MB free disk space
          </li>
        </ul>
      </div>

      {/* Troubleshooting */}
      <div className="bg-white rounded-xl shadow-lg p-6 border">
        <h2 className="text-2xl font-bold mb-4 text-gray-800">🔧 Troubleshooting</h2>
        <div className="space-y-4">
          <div>
            <h3 className="font-semibold text-gray-800 mb-2">Add-in doesn't appear in PowerPoint</h3>
            <ol className="list-decimal pl-6 space-y-1 text-sm text-gray-600">
              <li>Go to PowerPoint → <strong>File</strong> → <strong>Options</strong></li>
              <li>Click <strong>Add-ins</strong></li>
              <li>At the bottom, select <strong>COM Add-ins</strong> → <strong>Go</strong></li>
              <li>Look for <strong>QuickQuiz</strong> and check the box to enable it</li>
            </ol>
          </div>
          
          <div>
            <h3 className="font-semibold text-gray-800 mb-2">"Cannot find or load" error</h3>
            <p className="text-sm text-gray-600 mb-2">This usually means prerequisites are missing:</p>
            <ul className="list-disc pl-6 space-y-1 text-sm text-gray-600">
              <li>Install <a href="https://dotnet.microsoft.com/download/dotnet-framework/net48" className="text-indigo-600 hover:underline" target="_blank" rel="noopener noreferrer">.NET Framework 4.8</a></li>
              <li>Install <a href="https://aka.ms/vstor" className="text-indigo-600 hover:underline" target="_blank" rel="noopener noreferrer">VSTO Runtime</a></li>
              <li>Restart PowerPoint after installation</li>
            </ul>
          </div>
        </div>
      </div>

      {/* Support */}
      <div className="bg-gradient-to-r from-gray-50 to-gray-100 rounded-xl p-6 border">
        <h2 className="text-xl font-bold mb-2 text-gray-800">Need Help?</h2>
        <p className="text-gray-600 mb-4">
          For additional support or to report issues:
        </p>
        <div className="flex flex-wrap gap-3">
          <a
            href="https://github.com/henrykemp1977/quickquiz/issues"
            className="text-indigo-600 hover:underline text-sm font-medium"
            target="_blank"
            rel="noopener noreferrer"
          >
            🐛 Report an Issue
          </a>
          <a
            href="https://github.com/henrykemp1977/quickquiz"
            className="text-indigo-600 hover:underline text-sm font-medium"
            target="_blank"
            rel="noopener noreferrer"
          >
            📖 View Documentation
          </a>
        </div>
      </div>
    </div>
  );
}

